import React, { useEffect, useRef } from "react";

/**
 * Reusable confirmation popup.
 *
 * Props:
 * - isOpen: boolean
 * - title?: string
 * - message?: string | ReactNode
 * - confirmLabel?: string (default: "Yes")
 * - cancelLabel?: string (default: "Cancel")
 * - onConfirm: () => void
 * - onCancel: () => void
 * - isDestructive?: boolean (confirm button styled as danger)
 * - closeOnOverlayClick?: boolean (default: false)
 * - confirmDisabled?: boolean (disables the confirm button + Enter shortcut,
 *   e.g. when the message contains a gating input the user must fill)
 */
function ConfirmPopup({
    isOpen,
    title = "Are you sure?",
    message = "",
    confirmLabel = "Yes",
    cancelLabel = "Cancel",
    onConfirm,
    onCancel,
    isDestructive = false,
    closeOnOverlayClick = false,
    confirmDisabled = false,
}) {
    const confirmBtnRef = useRef(null);

    // Focus the confirm button when opened — but only if it's actually
    // enabled. If the caller supplies a gated flow (e.g. type-to-confirm),
    // stealing focus to a disabled button would just be annoying.
    useEffect(() => {
        if (isOpen && !confirmDisabled && confirmBtnRef.current) {
            confirmBtnRef.current.focus();
        }
    }, [isOpen, confirmDisabled]);

    // Keyboard shortcuts: Enter = confirm, Esc = cancel
    useEffect(() => {
        if (!isOpen) return;

        const onKeyDown = (e) => {
            if (e.key === "Escape") {
                e.preventDefault();
                onCancel?.();
            } else if (e.key === "Enter") {
                e.preventDefault();
                if (!confirmDisabled) onConfirm?.();
            }
        };

        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [isOpen, onCancel, onConfirm, confirmDisabled]);

    if (!isOpen) return null;

    const handleOverlayClick = () => {
        if (closeOnOverlayClick) onCancel?.();
    };

    return (
        <div className="confirm-popup-overlay" onClick={handleOverlayClick}>
            <div
                className="confirm-popup-box"
                role="dialog"
                aria-modal="true"
                aria-labelledby="confirm-popup-title"
                aria-describedby="confirm-popup-message"
                onClick={(e) => e.stopPropagation()} // prevent overlay click bubbling
            >
                <h3 id="confirm-popup-title" style={{ marginTop: 0 }}>{title}</h3>
                {message && (
                    <p id="confirm-popup-message" style={{ marginBottom: 16 }}>
                        {message}
                    </p>
                )}

                <div className="confirm-popup-actions">
                    <button className="confirm-popup-btn confirm-popup-btn-ghost" onClick={onCancel}>
                        {cancelLabel}
                    </button>
                    <button
                        ref={confirmBtnRef}
                        className={`confirm-popup-btn ${isDestructive ? "confirm-popup-btn-danger" : "confirm-popup-btn-primary"}`}
                        onClick={onConfirm}
                        disabled={confirmDisabled}
                        aria-disabled={confirmDisabled}
                    >
                        {confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default ConfirmPopup;
